import { lazy, Suspense } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { MAIN_PAGES, suggestPath } from "@/lib/redirects";
import { STATE_INDEX } from "@/lib/states-index";

// The same answer box as the home page, loaded only when someone lands here, so the
// not-found page (which sits in the root layout every page downloads) stays light.
const CheckArea = lazy(() =>
  import("@/components/zip-checker").then((m) => ({ default: m.CheckArea })),
);

/** The three things anyone arrives to do, in the words the home page and header use. */
const TASKS = [
  {
    n: "01",
    to: "/tick-bite-tool",
    title: "I found a tick on me",
    body: "Take it out, note the date, and see what to watch for.",
  },
  {
    n: "02",
    to: "/tick-identification",
    title: "What tick is this?",
    body: "Answer what you can see and narrow eight species down.",
  },
  {
    n: "03",
    to: "/prevention",
    title: "How do I avoid them?",
    body: "Repellent, treated clothing and the daily tick check.",
  },
] as const;

/**
 * What a wrong address shows: not an apology and a single link, but the site's front
 * door. A "did you mean" line when the address is a near miss, the answer box, the three
 * tasks, and every state, so whatever someone came for is one tap away.
 *
 * Likely guesses ("/tick-bite", "/identify", "/ga") never get here: src/lib/redirects.ts
 * sends them to the right page first.
 */
export function NotFoundPage({ kind = "page" }: { kind?: "page" | "guide" | "state" | "county" }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const suggestion = suggestPath(pathname);
  const heading =
    kind === "state"
      ? "We don't have a page for that state."
      : kind === "county"
        ? "We don't have a page for that county."
        : "There’s nothing at this address.";

  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 pt-12 sm:px-6 sm:pt-16">
      <header className="max-w-3xl">
        <p className="label-caps text-rust-ink">Page not found</p>
        <h1 className="mt-3 font-display text-[2.4rem] font-medium leading-[1.05] tracking-[-0.02em] text-foreground sm:text-[3.25rem]">
          {heading}
        </h1>
        {suggestion ? (
          <p className="mt-5 text-lg leading-relaxed text-foreground">
            Did you mean{" "}
            <Link to={suggestion} className="link font-semibold">
              {labelFor(suggestion)}
            </Link>
            ?
          </p>
        ) : (
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
            It may have moved. Everything on the site is one tap from here.
          </p>
        )}
      </header>

      <section aria-labelledby="nf-search" className="mt-10 max-w-xl">
        <h2 id="nf-search" className="label-caps text-muted-foreground">
          Check where you live
        </h2>
        <div className="mt-3">
          <Suspense
            fallback={
              <p className="text-[0.9375rem] text-muted-foreground">
                Type a ZIP code, city or state on the{" "}
                <Link to="/" className="link font-semibold">
                  home page
                </Link>
                .
              </p>
            }
          >
            <CheckArea compact />
          </Suspense>
        </div>
      </section>

      <section aria-labelledby="nf-tasks" className="mt-12">
        <h2 id="nf-tasks" className="label-caps border-t border-rule pt-3 text-muted-foreground">
          What do you need?
        </h2>
        <ol className="mt-2 sm:grid sm:grid-cols-3 sm:gap-x-8">
          {TASKS.map((t) => (
            <li key={t.to} className="border-b border-border sm:border-b-0">
              <Link to={t.to} className="group block py-4">
                <span className="tabular font-display text-lg text-rust-ink">{t.n}</span>
                <span className="mt-1 block font-display text-[1.375rem] font-medium leading-tight text-foreground group-hover:text-primary">
                  {t.title}
                </span>
                <span className="mt-1 block text-[0.9375rem] leading-relaxed text-muted-foreground">
                  {t.body}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="nf-states" className="mt-12">
        <h2 id="nf-states" className="label-caps border-t border-rule pt-3 text-muted-foreground">
          Every state
        </h2>
        <ul className="mt-4 columns-2 gap-x-8 text-[0.9375rem] sm:columns-3 lg:columns-4">
          {STATE_INDEX.map((s) => (
            <li key={s.slug} className="break-inside-avoid py-1">
              <Link to="/states/$state" params={{ state: s.slug }} className="link">
                {s.name}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <nav aria-label="More of the guide" className="mt-12 border-t border-border pt-4">
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {MAIN_PAGES.filter(
            (p) => p.path !== "/states" && !TASKS.some((t) => t.to === p.path),
          ).map((p) => (
            <li key={p.path}>
              <Link to={p.path} className="link">
                {p.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

function labelFor(path: string): string {
  const page = MAIN_PAGES.find((p) => p.path === path);
  if (page) return page.label;
  const state = STATE_INDEX.find((s) => `/states/${s.slug}` === path);
  if (state) return `Lone Star ticks in ${state.name}`;
  return path;
}
